import { AxiosResponse } from "axios";
import api from "../api/base-api";
import refreshAxiosInstance from "../api/refersh-api";
import { getBaseUrl } from "../../utils/common";

const getAll = (page: number, limit: number, dataFilter): Promise<AxiosResponse> => {
    //Added temporarily to test error screen
    const urlParams = new URLSearchParams(window.location.search);
    const tempError = urlParams.get("tempError");

    return api.post("/applications/developer" + (tempError === "component" ? "invalid" : ""), {
        page,
        limit,
        name: dataFilter.name,
    });
};

const getAppVersions = (id: string, page: number, limit: number): Promise<AxiosResponse> => {
    return api.get(`/applications/developer/${id}/versionings`, { params: { page, limit } });
};

const getById = (id: string): Promise<AxiosResponse> => {
    return api.get(`/applications/developer/${id}`);
};

const getOemAppById = (id: string, versionId: number): Promise<AxiosResponse> => {
    const version = versionId ? `/version/${versionId}` : "/version";
    return api.get(`/applications/oem/${id}${version}`);
};

const sortAppScreenshots = (id: string, data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.put(`/applications/${id}/media/sort`, data);
};

const updateMediaById = (
    id: string,
    data: FormData,
    onProgress: (arg0: ProgressEvent<EventTarget>) => void
): Promise<AxiosResponse> => {
    return api.put(`/applications/${id}/media`, data, {
        headers: {
            "Content-type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: ProgressEvent) => onProgress(progressEvent),
    });
};

const updateVersioningById = (
    id: string,
    data: FormData,
    onProgress: (arg0: ProgressEvent<EventTarget>) => void
): Promise<AxiosResponse> => {
    return api.post(`/applications/${id}/versionings`, data, {
        headers: {
            "Content-type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: ProgressEvent) => onProgress(progressEvent),
    });
};

const deleteMediaById = (id: string, data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.delete(`/applications/${id}/media`, {
        data: data,
    });
};

const create = (data: FormData): Promise<AxiosResponse> => {
    return api.post("/applications/initiate", data);
};

const generateKeyPair = (appId: string, data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.post(`/signing/createKeyPairs/${appId}`, data, { responseType: "arraybuffer" });
};

const updateDetails = (appId: string, data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.put(`/applications/${appId}/details`, data);
};
const updateTestCredential = (
    appId: string,
    data: Record<string, unknown>
): Promise<AxiosResponse> => {
    return api.put(`/applications/${appId}/tester/credentials`, data);
};

const checkAvailableNewApplicationName = (name: string): Promise<AxiosResponse> => {
    return api.get("/applications/validate/name", {
        params: {
            name,
        },
    });
};

const checkAvailableEditApplicationName = (appId: string, name: string): Promise<AxiosResponse> => {
    return api.get(`/applications/${appId}/validate/name`, {
        params: {
            name,
        },
    });
};

const getAllOemApps = (
    data: Record<string, unknown>,
    excludeAppsWithZeroDownloads = false
): Promise<AxiosResponse> => {
    return api.post(
        `/applications/oem?filterOutAppsWithZeroInstalls=${excludeAppsWithZeroDownloads}`,
        data
    );
};

const getOemAppsCount = (
    data: Record<string, unknown>,
    excludeAppsWithZeroDownloads = false
): Promise<AxiosResponse> => {
    return api.post(
        `/applications/oem/status-count?filterOutAppsWithZeroInstalls=${excludeAppsWithZeroDownloads}`,
        data
    );
};

const updateDevAppCountries = (
    appId: string,
    data: Record<string, unknown>
): Promise<AxiosResponse> => {
    return api.put(`/applications/${appId}/country`, data);
};

const updateOemAppCountries = (
    appId: string,
    data: Record<string, unknown>
): Promise<AxiosResponse> => {
    return api.put(`/applications/oem/${appId}/whitelist/country`, data);
};

const updateOemAppVehicles = (
    appId: string,
    data: Record<string, unknown>
): Promise<AxiosResponse> => {
    return api.put(`/applications/oem/${appId}/whitelist/vehicle`, data);
};

const updateAppStatus = (appId: string, data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.patch(`/applications/oem/${appId}`, data);
};

const updatePriceDetails = (
    appId: string,
    data: Record<string, unknown>
): Promise<AxiosResponse> => {
    return api.put(`/applications/${appId}/pricing`, data);
};

const getAllTesterApps = (data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.post("applications/tester", data);
};

const getAllTesterAppDetails = (appId: string, versionId: string): Promise<AxiosResponse> => {
    const version = versionId ? `/version/${versionId}` : "";
    return api.get(`applications/tester/details/${appId}${version}`);
};

const getTesterAppVersions = (id: string, page: number, limit: number): Promise<AxiosResponse> => {
    return api.get(`/applications/history/${id}`, { params: { page, limit } });
};

const getValidationReportPerApp = (id: string): Promise<AxiosResponse> => {
    return api.get(`/applications/developer/validation_reports/${id}`);
};

const getCountries = (id: string): Promise<AxiosResponse> => {
    return api.get(`/applications/oem/countries/list?oemId=${id}`, {
        params: { limit: 500 },
        baseURL: `${getBaseUrl()}/api/V2`,
    });
};

const getBrands = (): Promise<AxiosResponse> => {
    return api.get("/vehicles/brands", { params: { limit: 500 } });
};

const getVehicleModels = (id: string): Promise<AxiosResponse> => {
    return api.get(`/vehicles/models/${id}`);
};

const registerIV = (data: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.post("/auth/register/ivuser", data);
};

const recoverPassword = (data: Record<string, unknown>, token: string): Promise<AxiosResponse> => {
    return refreshAxiosInstance.post("/auth/V1/recover-password", data, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

const resendPasswordRecoveryEmailApi = (data: Record<string, unknown>): Promise<AxiosResponse> => {
    return refreshAxiosInstance.post("/auth/V1/recover-password-email", data);
};

const checkIfEmailCanBeResentNowApi = (data: Record<string, unknown>): Promise<AxiosResponse> => {
    return refreshAxiosInstance.post("/auth/V1/recover-password-email/can-resend", data);
};

const deleteAppApi = (id: string, token: string): Promise<AxiosResponse> => {
    return api.delete(`/applications/${id}/draft`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

const submitFeedbackApi = (
    data: Record<string, unknown>,
    token: string
): Promise<AxiosResponse> => {
    return refreshAxiosInstance.post("/auth/V1/submit-feedback", data, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

const downloadAPKByVersion = (data: Record<string, unknown>, token: string) => {
    return api.post("/applications/download-apk", data, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
    });
};

const fetchAgreementApi = (): Promise<AxiosResponse> => {
    return api.get("/agreements");
};

const uploadCertificateAPI = (
    id: string,
    data: FormData,
    onProgress: (arg0: ProgressEvent<EventTarget>) => void
): Promise<AxiosResponse> => {
    return api.post(`/signing/${id}/developer/certificate`, data, {
        headers: {
            "Content-type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: ProgressEvent) => onProgress(progressEvent),
    });
};

const fetchCertificateAPI = (id: string): Promise<AxiosResponse> => {
    return api.get(`/applications/${id}/certificate`);
};

export {
    getAll,
    create,
    generateKeyPair,
    updateVersioningById,
    updateDetails,
    updateTestCredential,
    getById,
    updateMediaById,
    deleteMediaById,
    sortAppScreenshots,
    checkAvailableNewApplicationName,
    checkAvailableEditApplicationName,
    getAppVersions,
    getAllOemApps,
    getOemAppsCount,
    updateOemAppCountries,
    getOemAppById,
    updateAppStatus,
    updateOemAppVehicles,
    updateDevAppCountries,
    updatePriceDetails,
    getAllTesterApps,
    getAllTesterAppDetails,
    getTesterAppVersions,
    getValidationReportPerApp,
    getCountries,
    getBrands,
    getVehicleModels,
    registerIV,
    recoverPassword,
    resendPasswordRecoveryEmailApi,
    checkIfEmailCanBeResentNowApi,
    deleteAppApi,
    submitFeedbackApi,
    downloadAPKByVersion,
    fetchAgreementApi,
    uploadCertificateAPI,
    fetchCertificateAPI,
};
